import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeftRight, Eye, HeartHandshake, X, RotateCcw } from "lucide-react";
import { WeightedCompatibilityScore } from "./WeightedCompatibilityScore";
import { WeightedCompatibilityResult } from "@/utils/weightedCompatibility";
import { PriorityBadges } from "./PriorityBadges";
import { getScoreColor } from "@/utils/compatibilityColors";
import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useSignedPhotoUrl } from "@/lib/photoUrl";

interface Profile {
  id: string;
  first_name: string;
  age: number;
  location: string;
  profile_picture_url?: string;
  gender: string;
  religiosity_level: string;
  education_level: string;
  seeking_preferences_weights?: any;
}

interface MatchPairCardProps {
  profile1: Profile;
  profile2: Profile;
  compatibilityResult: WeightedCompatibilityResult;
  onCreateMatch: (profile1Id: string, profile2Id: string) => void;
  onDismiss?: (profile1Id: string, profile2Id: string) => void;
  onUndismiss?: (profile1Id: string, profile2Id: string) => void;
  onViewProfile: (profileId: string) => void;
  showActions?: boolean;
  isDismissed?: boolean;
}

function ProfileSection({ profile }: { profile: Profile }) {
  const signedProfilePicture = useSignedPhotoUrl(profile.profile_picture_url);
  return (
    <div className="flex-1 space-y-2">
      <div className="flex items-center gap-3">
        <Avatar className="h-16 w-16">
          <AvatarImage src={signedProfilePicture ?? undefined} alt={profile.first_name} />
          <AvatarFallback>{profile.first_name[0]}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <h3 className="font-semibold text-lg">
            {profile.first_name}, {profile.age}
          </h3>
          <p className="text-sm text-muted-foreground">{profile.location}</p>
          <div className="flex gap-2 mt-1">
            <Badge variant="outline" className="text-xs">
              {profile.religiosity_level}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {profile.education_level}
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MatchPairCard({
  profile1,
  profile2,
  compatibilityResult,
  onCreateMatch,
  onDismiss,
  onUndismiss,
  onViewProfile,
  showActions = true,
  isDismissed = false,
}: MatchPairCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Card className="p-6">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <ProfileSection profile={profile1} />

            <div className="flex flex-col items-center justify-center px-4">
              <ArrowLeftRight className="h-6 w-6 text-primary" />
              <div className="text-center mt-2">
                <div className={cn("text-2xl font-bold", getScoreColor(compatibilityResult.score))}>
                  {compatibilityResult.score}%
                </div>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-xs mt-1">
                    {isOpen ? "Hide" : "Details"}
                  </Button>
                </CollapsibleTrigger>
              </div>
            </div>

            <ProfileSection profile={profile2} />
          </div>

          <CollapsibleContent>
            <div className="pt-4 border-t space-y-4">
              {compatibilityResult.weightedFactors.mutualPriorities.length > 0 && (
                <div className="rounded-lg bg-green-500/10 p-3 border border-green-500/20">
                  <p className="text-sm font-semibold text-green-700 dark:text-green-400 mb-1">
                    ✨ They both prioritize the same things!
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Mutual priorities: {compatibilityResult.weightedFactors.mutualPriorities.join(", ")}
                  </p>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                {profile1.seeking_preferences_weights && (
                  <PriorityBadges
                    weights={profile1.seeking_preferences_weights}
                    firstName={profile1.first_name}
                  />
                )}
                {profile2.seeking_preferences_weights && (
                  <PriorityBadges
                    weights={profile2.seeking_preferences_weights}
                    firstName={profile2.first_name}
                  />
                )}
              </div>

              <WeightedCompatibilityScore
                result={compatibilityResult}
                showDetails={true}
                profile1Name={profile1.first_name}
                profile2Name={profile2.first_name}
              />
            </div>
          </CollapsibleContent>

          {showActions && (
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewProfile(profile1.id)}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View {profile1.first_name}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewProfile(profile2.id)}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View {profile2.first_name}
                </Button>
              </div>
              <div className="flex gap-2">
                {onDismiss && !isDismissed && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDismiss(profile1.id, profile2.id)}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Dismiss
                  </Button>
                )}
                {onUndismiss && isDismissed && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onUndismiss(profile1.id, profile2.id)}
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Restore
                  </Button>
                )}
                {!isDismissed && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => onCreateMatch(profile1.id, profile2.id)}
                    disabled={compatibilityResult.dealBreakerFailed || compatibilityResult.dealBreakers.length > 0}
                  >
                    <HeartHandshake className="h-4 w-4 mr-1" />
                    Create Match
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </Collapsible>
    </Card>
  );
}
